import { useCallback, useRef, useState } from 'react';

/**
 * The two things a `RefreshControl` needs, from one async function.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A HOOK AND NOT FOUR LINES AT EACH CALL SITE
 * ---------------------------------------------------------------------------
 * A pull-to-refresh is a `refreshing` boolean and an `onRefresh` callback, and
 * the boolean has to be the caller's own - driving it off some query's
 * `loading` is the mistake this exists to stop. Home did exactly that: the
 * spinner was wired to the bookings' loading flag while the pull also re-read
 * the catalogue, so it retracted while the catalogue was still in flight and
 * the prices changed a moment after the gesture ended. That reads as the app
 * correcting itself rather than as the refresh somebody asked for.
 *
 * So the flag tracks THE PULL, not any one request inside it. It goes true on
 * the gesture and false when everything the pull started has settled.
 *
 * ---------------------------------------------------------------------------
 * IT NEVER REJECTS, AND NEVER SPINS FOREVER
 * ---------------------------------------------------------------------------
 * A failed refresh is not something the puller can act on - the screen still
 * shows what it showed, which is the right outcome. What they must never get
 * is a spinner that keeps turning, so the flag is cleared whatever happens.
 *
 * A second pull while one is already running is ignored rather than queued: on
 * a slow connection an impatient customer will pull three times, and three
 * overlapping refreshes end in whichever order they end, with the last one to
 * finish deciding the flag.
 */
export function usePullToRefresh(run: () => Promise<unknown>): {
  refreshing: boolean;
  onRefresh: () => void;
} {
  const [refreshing, setRefreshing] = useState(false);

  /*
   * A ref as well as the state, because `onRefresh` fires from a gesture and
   * would read a `refreshing` captured a render ago - the exact window two
   * quick pulls land in.
   */
  const running = useRef(false);

  const onRefresh = useCallback(() => {
    if (running.current) {
      return;
    }
    running.current = true;
    setRefreshing(true);

    Promise.resolve()
      .then(run)
      .catch(() => undefined)
      .finally(() => {
        running.current = false;
        setRefreshing(false);
      });
  }, [run]);

  return { refreshing, onRefresh };
}

export default usePullToRefresh;
